import {AfterViewInit, Component, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MatSort, MatSortModule, Sort} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {CommonDirectives} from '@app/shared/directives/common-directives';
import {UserStatusInfo} from '../../interfaces/userStatusInfo.interface';
import {LoadSpinnerWrapperComponent} from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import {UpperCasePipe} from '@app/shared/pipes/upperCase.pipe';
import {DateFormatPipe} from '@app/shared/pipes/dateFormat.pipe';
import {AppState} from '@app/core/store/app.state';
import {Store} from '@ngrx/store';
import {PageSearchSortOptions} from '@app/shared/interfaces/pageSearchSortOptions.interface';
import {FetchAllSystemUsers} from '../../store/admin.actions';
import { selectAllSystemUsers } from '../../store/admin.selectors';
import { catchError, firstValueFrom, forkJoin, map, Observable, of, Subscription, switchMap } from 'rxjs';
import { SearchComponent } from '@app/shared/components/search/search.component';
import { PaginationComponent } from "@app/shared/components/pagination/pagination.component";
import { UserDetailsService } from '@app/shared/services/userDetails.service';
import { getAccountType } from '@app/shared/services/helpers';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { UserMgmtUserInfoModalComponent } from '../user-mgmt-user-info-modal/user-mgmt-user-info-modal.component';
import { AccountTypes } from '@app/shared/enums/accountTypes.enum';
import { ReferralUser } from '@app/core/interfaces/user.interface';
import {MatButtonModule} from '@angular/material/button';
import {MatMenuModule} from '@angular/material/menu';
import {AdminService} from '../../services/admin.service';
import {ToastService} from '@app/core/services/toast.service';
import {
  exportUserManagementResults,
  UserManagementExportFormat
} from './user-list-export';

import {
  FilterCheckboxComponent
} from "@app/shared/components/filter-checkbox/filter-checkbox/filter-checkbox.component";
import {UserDateType, UserStatus} from "@app/shared/enums/userStatus.enum";
import {
  DateTypeFilterComponent
} from "@app/shared/components/filter-checkbox/date-type-filter/date-type-filter/date-type-filter.component";
import {OptionItem} from "@app/shared/interfaces/optionItem.interface";
import {DateTypeFilterFormValue} from "@app/shared/interfaces/dateTypeFilterFormValue.interface";
import {UserFilterOptions} from "@app/shared/interfaces/userFilterOptions.interface";

@Component({
  selector: 'app-user-list',
  imports: [
    MatTableModule,
    MatSortModule,
    CommonDirectives,
    LoadSpinnerWrapperComponent,
    UpperCasePipe,
    DateFormatPipe,
    SearchComponent,
    PaginationComponent,
    MatDialogModule,
    MatButtonModule,
    MatMenuModule,
    FilterCheckboxComponent,
    DateTypeFilterComponent
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss'
})
export class UserListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChildren(FilterCheckboxComponent) filterCheckboxes!: QueryList<FilterCheckboxComponent>;

  @Input() title!: string;

  readonly defaultPageSize: number = 25;
  private readonly exportPageSize: number = 100;
  displayedColumns: string[] = ['accountType', 'userName', 'company', 'registration', 'lastLogin', 'status'];
  tableDataSource = new MatTableDataSource<UserStatusInfo>();
  subscriptions: Subscription = new Subscription();
  pageSearchSortOptions: PageSearchSortOptions = {
    pageNumber: 1,
    pageSize: this.defaultPageSize,
    searchTerm: '',
    sortField: 'lastLogin',
    sortDirection: 'desc',
    secondarySortField: '',
    secondarySortDirection: 'desc'
  }
  filters: UserFilterOptions = {
    accountTypes: [],
    status: []
  }

  loading: boolean = false;
  readonly noResultsMessage: string = 'There are no users at this time.';
  readonly noResultsMessageAfterSearch: string = 'No records found. Please adjust your search or filters and try again.';
  noResultsMessageToShow: string = this.noResultsMessage;
  totalCount: number = 0;
  totalPages: number = 0;
  isExporting: boolean = false;
  statusItems: OptionItem[] = [
    { id: 1, name: 'Active' },
    { id: 3, name: 'Denied' },
    { id: 2, name: 'Inactive' }
  ];
  userTypeItems: OptionItem[] = [
    { id: 0, name: 'Admin' },
    { id: 4, name: 'Referral - Referring Clinic' },
    { id: 1, name: 'Work Comp - Case Manager' },
    { id: 2, name: 'Work Comp - Employer of Work Comp Patient' },
    { id: 3, name: 'Work Comp - Insurance Adjuster' },
  ]

  constructor(
    private store: Store<AppState>,
    private userDetailsService: UserDetailsService,
    private dialog: MatDialog,
    private adminService: AdminService,
    private toastService: ToastService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.subscribeToAllSystemUsers();
  }

  ngAfterViewInit(): void {
    // Set initial sort
    this.sort.active = this.pageSearchSortOptions.sortField;
    this.sort.direction = this.pageSearchSortOptions.sortDirection;

    // Manually trigger sort change to fetch sorted data
    this.onSortChange({ active: this.sort.active, direction: this.sort.direction });
  }

  onSortChange(sort: Sort): void {
    const { active, direction } = sort;
    const updatedPageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      sortField: active,
      sortDirection: direction as 'asc' | 'desc'
    };
    this.pageSearchSortOptions = updatedPageSearchSortOptions;
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters  }}));
  }
  getStatusName(key: number){
    return this.statusItems.find(id => id.id === key)?.name ?? '';
  }
  getUserTypeName(key: number){
    return this.userTypeItems.find(id => id.id === key)?.name ?? '';
  }
  get getDateFilterName(){
    let label: string = '';
    switch (this.filters.dateTypeFilter?.dateType) {
      case UserDateType.REGISTRATION:
        label = 'Registration';
        break;
        case UserDateType.LAST_LOGIN:
          label = 'Last Login';
          break;
    }
    return label;
  }


  get hasActiveDateFilter(): boolean {
    return Boolean(this.filters.dateTypeFilter?.dateType) && this.hasDateRange(this.filters.dateTypeFilter);
  }

  get dataFiltered(): boolean {
    return this.filters.accountTypes.length !== 0 || this.filters.status.length !== 0 || this.hasActiveDateFilter;
  }

  subscribeToAllSystemUsers() {
    this.subscriptions.add(
      this.store.select(selectAllSystemUsers)
        .pipe(
          switchMap(usersResponse => {
            if (!usersResponse) {
              return of(null);
            }

            return this.populateReferralClinics(usersResponse.data.items).pipe(
              map(users => ({ users, totalCount: usersResponse.data.totalCount }))
            );
          })
        )
        .subscribe(result => {
          if (!result) {
            return;
          }

          this.tableDataSource.data = result.users;
          this.totalCount = result.totalCount;
          this.totalPages = Math.ceil(this.totalCount / this.pageSearchSortOptions.pageSize);
          if (this.pageSearchSortOptions.searchTerm.trim() || this.hasAnyFilters()) this.noResultsMessageToShow = this.noResultsMessageAfterSearch;
          else this.noResultsMessageToShow = this.noResultsMessage;
        })
    );
  }

  private hasAnyFilters() : boolean {
    return this.filters.status.length > 0 || this.hasActiveDateFilter || this.filters.accountTypes.length > 0;
  }

  private hasDateRange(dateTypeFilter: DateTypeFilterFormValue | undefined): boolean {
    return Boolean(dateTypeFilter?.startDate || dateTypeFilter?.endDate);
  }

  private populateReferralClinics(users: UserStatusInfo[]): Observable<UserStatusInfo[]> {
    const usersWithClinics$ = users.map(user => {
      if (user.accountTypeId !== AccountTypes.REFERRAL_PARTNER) {
        return of(user);
      }

      return this.userDetailsService
        .getUserDetails$(user.userSubId, AccountTypes.REFERRAL_PARTNER)
        .pipe(
          map(userDetails => {
            const referralUser = userDetails as ReferralUser;
            const clinics = [referralUser.firstClinicId, referralUser.secondClinicId]
              .filter((clinic): clinic is string => typeof clinic === 'string' && clinic.trim().length > 0)
              .map(clinic => clinic.trim());

            return {
              ...user,
              company: clinics.join(', ')
            };
          }),
          catchError(error => {
            console.error(`Unable to load clinics for referral user ${user.userSubId}.`, error);
            return of(user);
          })
        );
    });

    return usersWithClinics$.length > 0 ? forkJoin(usersWithClinics$) : of([]);
  }

  onSearch(searchTerm: string) {
    const updatedPageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      searchTerm: searchTerm,
      pageNumber: 1 // Reset to first page on new search
    };
    this.pageSearchSortOptions = updatedPageSearchSortOptions;
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters  }}));
  }

  onPageChange(newPage: number) {
    const updatedPageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: newPage
    };
    this.pageSearchSortOptions = updatedPageSearchSortOptions;
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters  }}));
  }


  onStatusFilter(event: number[]){
    this.filters = {...this.filters, status:  event.map(num => num as UserStatus)};
    this.pageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: 1
    };
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters }}));
  }

  onAccountTypeFilter(event: number[]){
    this.filters = {...this.filters, accountTypes: event.map(num => num as AccountTypes)};
    this.pageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: 1
    };
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters }}));
  }

  onDateFilter(event: DateTypeFilterFormValue){
    const dateTypeFilter = event.dateType && this.hasDateRange(event) ? event : undefined;
    this.filters = {...this.filters, dateTypeFilter};
    this.pageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: 1
    };
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters }}));
  }

  removeStatusTag(tag: UserStatus): void {
    const newTags = this.filters.status.filter(status => status !== tag);
    this.filters = {...this.filters, status: newTags}
    this.pageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: 1
    };
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: {
        pageSearchSortOptions: this.pageSearchSortOptions,
        filter: this.filters
      }}));
  }

  removeUserTypeTag(tag: AccountTypes): void {
    const newTags = this.filters.accountTypes.filter(account => account !== tag);
    this.filters = {...this.filters, accountTypes: newTags}
    this.pageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: 1
    };
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: {
        pageSearchSortOptions: this.pageSearchSortOptions,
        filter: this.filters
      }}));
  }

  removeDateTag(): void {
    this.filters = {...this.filters, dateTypeFilter: undefined};
    this.pageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: 1
    };
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: {
        pageSearchSortOptions: this.pageSearchSortOptions,
        filter: this.filters
      }}));
  }

  clearAllFilters(): void {
    this.filters = {
      accountTypes: [],
      status: [],
    };
    this.filterCheckboxes.forEach(filterCheckbox => filterCheckbox.resetSelection());
    this.pageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: 1
    };
    this.store.dispatch(FetchAllSystemUsers({userPageRequest: {
      pageSearchSortOptions: this.pageSearchSortOptions,
      filter: this.filters
    }}));
  }

  async exportUsers(format: UserManagementExportFormat): Promise<void> {
    if (this.isExporting || this.totalCount === 0) {
      return;
    }

    this.isExporting = true;
    try {
      const users = await this.fetchAllUsersForExport();
      if (users.length === 0) {
        this.toastService.showWarningToast('There are no users to export.');
        return;
      }

      await exportUserManagementResults(users, format);
    } catch (error: unknown) {
      console.error('Unable to export users.', error);
      this.toastService.showErrorToast('Unable to export users. Please try again.');
    } finally {
      this.isExporting = false;
    }
  }

  private async fetchAllUsersForExport(): Promise<UserStatusInfo[]> {
    const exportOptions: PageSearchSortOptions = {
      ...this.pageSearchSortOptions,
      pageNumber: 1,
      pageSize: this.exportPageSize
    };
    const firstPage = await firstValueFrom(this.adminService.getAllSystemUsers$({pageSearchSortOptions: exportOptions, filter: this.filters}));
    if (!firstPage.succeeded) {
      throw new Error(firstPage.errorMessages.join(' ') || 'Unable to load users for export.');
    }

    const users = await firstValueFrom(this.populateReferralClinics(firstPage.data.items));
    const responsePageSize = firstPage.data.pageSize || this.exportPageSize;
    const pageCount = Math.ceil(firstPage.data.totalCount / responsePageSize);

    for (let pageNumber = 2; pageNumber <= pageCount; pageNumber++) {
      const page = await firstValueFrom(this.adminService.getAllSystemUsers$({pageSearchSortOptions: {
        ...exportOptions,
        pageNumber
      }, filter: this.filters}));
      if (!page.succeeded) {
        throw new Error(page.errorMessages.join(' ') || 'Unable to load users for export.');
      }

      const pageUsers = await firstValueFrom(this.populateReferralClinics(page.data.items));
      users.push(...pageUsers);
    }

    return users;
  }

  openUserInfoModal(user: UserStatusInfo) {
    this.loading = true;
    this.userDetailsService.getUserDetails$(user.userSubId, getAccountType(user.accountTypeId))
      .subscribe(userDetails => {
        this.loading = false;
        const dialogRef = this.dialog.open(UserMgmtUserInfoModalComponent, {
          width: '600px',
          autoFocus: false,
          disableClose: true
        });
        dialogRef.componentInstance.userAccountTypeId = user.accountTypeId;
        dialogRef.componentInstance.userDetails = userDetails;
        dialogRef.componentInstance.onSuspendUserSuccess.subscribe(() => {
          this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters  }}));
        });
        dialogRef.componentInstance.onReactivateUserSuccess.subscribe(() => {
          this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters  }}));
        });
        dialogRef.afterClosed().subscribe(() => {
          dialogRef.componentInstance.onSuspendUserSuccess.unsubscribe();
          dialogRef.componentInstance.onReactivateUserSuccess.unsubscribe();
          this.store.dispatch(FetchAllSystemUsers({userPageRequest: { pageSearchSortOptions: this.pageSearchSortOptions, filter: this.filters  }}));
        });
      });
  }
}
